import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/system';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';

import type { LanguageSkill, Skill } from '~/shared/data-contract/skill';

import { SkillChipWithDrawer } from '../../../app/common/skills/SkillChipWithDrawer';
import { SpacingMultiple } from '../constants';
import { Headline, Text } from '../Typography';

type SkillsGroup = {
    readonly groupTitle: string;
    readonly skills: ReadonlyArray<Skill>;
};

export type HardSoftSkillsCollectionProps = {
    readonly skillsGroups: ReadonlyArray<SkillsGroup>;
};

export const StyledItem = styled(Grid)(({ theme }) => ({
    '& > *': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    alignItems: 'center',
    borderBottom: '1px solid',
    borderColor: theme.palette['cardborder'].main,
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    height: '52px',
    justifyContent: 'space-between',
    paddingInline: theme.spacing(SpacingMultiple.large),
}));

const StyledItemNoBorderOnLast = styled(ListItem)`
    &:last-child > ${StyledItem} {
        border-bottom: none;
    }
`;

export const HeadlineItem: FunctionComponent<{ readonly title: string }> = ({ title }) => (
    <StyledItem container={true}>
        <Text>{title}</Text>
    </StyledItem>
);

export const HardSoftSkillsCollection: FunctionComponent<HardSoftSkillsCollectionProps> = ({ skillsGroups }) => {
    return (
        <List disablePadding={true}>
            {skillsGroups.map(({ groupTitle, skills }) => (
                <ListItem
                    key={groupTitle}
                    disablePadding={true}
                    sx={{ alignItems: 'stretch', flexDirection: 'column' }}
                >
                    <HeadlineItem title={groupTitle} />
                    <List disablePadding={true}>
                        {skills.map((skill) => (
                            <ListItem key={skill.id} disablePadding={true}>
                                <StyledItem container={true}>
                                    <SkillChipWithDrawer
                                        isSynced={skill.isSynced}
                                        label={skill.title}
                                        skillId={skill.id}
                                        type={skill.skillType}
                                    />
                                </StyledItem>
                            </ListItem>
                        ))}
                    </List>
                </ListItem>
            ))}
        </List>
    );
};

type LanguageSkillsCollectionProps = {
    readonly getProficiencyLabel: (skill: LanguageSkill) => string;
    readonly languageSkills: ReadonlyArray<LanguageSkill>;
    readonly noBorderBottom?: boolean;
    readonly title: string;
};

export const LanguageSkillsCollection: FunctionComponent<LanguageSkillsCollectionProps> = ({
    getProficiencyLabel,
    languageSkills,
    noBorderBottom,
    title,
}) => {
    if (is.emptyArray(languageSkills)) {
        return null;
    }

    const ListItemComponent = noBorderBottom === true ? StyledItemNoBorderOnLast : ListItem;

    return (
        <Fragment>
            <StyledItem alignItems="center" container={true}>
                <Headline variant="h5">{title}</Headline>
            </StyledItem>
            <List disablePadding={true}>
                {languageSkills.map((skill) => (
                    <ListItemComponent key={skill.id} disablePadding={true}>
                        <StyledItem container={true}>
                            <SkillChipWithDrawer label={skill.title} skillId={skill.id} type={skill.skillType} />
                            <Text>{getProficiencyLabel(skill)}</Text>
                        </StyledItem>
                    </ListItemComponent>
                ))}
            </List>
        </Fragment>
    );
};
